<template>
    <div class="hero hero-product-detail w-full relative mb-0 md:mb-54px">
        <!-- Mobile Design -->
        <div class="md:hidden">
            <div class="relative">
              <!-- Hero Image Section -->
              <div class="relative w-full h-500px bg-gray-600">
                  <picture v-if="currentProduct && currentProduct.productBannerImage.length" class="bg-center absolute bg-cover bg-no-repeat left-0 top-0 w-full h-full z-0 hero-picture" :style="currentBackgroundStyle">
                      <img class="opacity-0 invisible w-full block absolute left-0 top-0" :src="currentProduct.productBannerImage[0].url" :alt="currentProduct.title" />
                  </picture>
              </div>

              <!-- Mobile Product Info Card -->
              <div class="px-5 bottom-1 w-full absolute z-20">
                <div v-if="currentProduct" class="bg-white px-6 shadow-figure2 mb-4">
                    <div class="flex flex-col">
                        <div class="my-4">
                            <!-- Product Variation Dropdown -->
                            <div class="relative" v-if="productInfo && productInfo.products && productInfo.products.length > 1">
                                <button 
                                    @click="toggleProductDropdown" 
                                    class="w-full text-left flex items-center justify-between"
                                    type="button"
                                >
                                    <div>
                                        <h3 class="block text-xxs text-lochmara font-bold">Product Variation:</h3>
                                        <h2 class="block text-28px text-shark font-bold">{{currentProduct.title}}</h2>
                                    </div>
                                    <svg 
                                        class="w-6 h-6 text-shark transition-transform duration-200 flex-shrink-0 ml-2" 
                                        :class="{'rotate-180': showProductDropdown}"
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                </button>
                                <!-- Dropdown Menu -->
                                <div 
                                    v-if="showProductDropdown" 
                                    class="absolute top-full left-0 right-0 mt-2 bg-white shadow-lg border border-iron2 rounded z-50 max-h-300px overflow-y-auto"
                                >
                                    <ul class="text-shuttle text-13px">
                                        <li 
                                            v-for="product in productInfo.products" 
                                            :key="product.slug"
                                            class="border-b border-blackhaze last:border-b-0"
                                            :class="{'bg-porcelain2': product.slug === currentProduct.slug}"
                                        >
                                            <router-link 
                                                :to="getProductLink(product)" 
                                                @click.native="showProductDropdown = false"
                                                class="block p-4 font-bold text-shuttle text-13px hover:bg-porcelain2 transition"
                                            >
                                                {{product.title}}
                                            </router-link>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <!-- Single Product Title (no dropdown) -->
                            <h2 v-else class="block text-28px text-shark font-bold">{{currentProduct.title}}</h2>
                        </div>
                        
                        <div class="-mx-6 border-t border-iron2"></div>

                        <div class="min-h-150px py-5 flex items-center relative">
                            <div v-if="displayingView === 'colors'" class="w-full">
                                <div class="overflow-x-auto -mx-6 px-6 scrollbar-hide">
                                    <div class="flex flex-nowrap gap-4 pb-2">
                                        <div v-if="hasOrderedProductColors" class="flex-shrink-0">
                                            <div class="flex flex-col gap-3">
                                                <div v-if="orderedProductColorGroups.core.length" class="flex flex-nowrap gap-3">
                                                    <router-link
                                                        v-for="color in orderedProductColorGroups.core"
                                                        :key="color.uid"
                                                        :to="getSwatchLink(currentProduct, color)"
                                                        class="block w-30px h-30px p-2 rounded-full cursor-pointer relative pd-color-picker__pallete flex-shrink-0"
                                                        :class="{'active': currentColor.uid === color.uid}"
                                                        :style="{backgroundColor: color.color}"
                                                    ></router-link>
                                                </div>
                                                <div v-if="orderedProductColorGroups.secondary.length" class="flex flex-nowrap gap-3">
                                                    <router-link
                                                        v-for="color in orderedProductColorGroups.secondary"
                                                        :key="color.uid"
                                                        :to="getSwatchLink(currentProduct, color)"
                                                        class="block w-30px h-30px p-2 rounded-full cursor-pointer relative pd-color-picker__pallete flex-shrink-0"
                                                        :class="{'active': currentColor.uid === color.uid}"
                                                        :style="{backgroundColor: color.color}"
                                                    ></router-link>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="currentProduct.availableProductStains.length" class="flex-shrink-0">
                                            <div class="flex flex-nowrap gap-3">
                                                <router-link 
                                                    v-for="stain in currentProduct.availableProductStains" 
                                                    :key="stain.uid"
                                                    :to="getSwatchLink(currentProduct, stain)" 
                                                    class="block w-30px h-30px p-2 rounded-full cursor-pointer relative pd-color-picker__pallete flex-shrink-0" 
                                                    :class="{'active': currentColor.uid === stain.uid}" 
                                                    :style="{backgroundColor: stain.color}"
                                                ></router-link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-1 flex items-center" v-if="currentColor">
                                    <img
                                        v-if="showSpectrumLogo"
                                        :src="spectrumLogo"
                                        alt="Spectrum"
                                        class="h-4 w-auto mr-2 flex-shrink-0"
                                    />
                                    <span class="block text-shuttle"><strong class="text-shark font-bold"></strong>{{currentColor.title}}</span>
                                </div>
                            </div>
                            <div v-else-if="displayingView === 'primed'" class="w-full">
                                <div class="overflow-x-auto -mx-6 px-6 scrollbar-hide">
                                    <div class="flex pb-3">
                                        <router-link :to="getSwatchLink(currentProduct, primedColor)" class="block w-30px h-30px p-2 rounded-full cursor-pointer relative pd-color-picker__pallete" :class="{'active': currentColor.uid === primedColor.uid}" :style="{backgroundColor: primedColor.color}"></router-link>
                                    </div>
                                </div>
                                <div class="mt-1">
                                    <span class="block text-shuttle"><strong class="text-shark font-bold"></strong>{{primedColor.title}}</span>
                                </div>
                            </div>
                            <div v-else-if="displayingView === 'sizes'" class="w-full">
                                <div class="overflow-x-auto -mx-6 px-6 py-3 my-4 scrollbar-hide">
                                    <div class="flex flex-nowrap gap-2 h-30px items-center">
                                        <div v-for="(size, index) in currentProduct.availableProductSizes" :key="index" class="flex-shrink-0 min-w-120px">
                                            <div class="relative cursor-pointer pd-size-picker__item">
                                                <input :id="'size-' + index" type="radio" name="pd-sizes" class="absolute inset-0 left-0 top-0 w-full h-full opacity-0 cursor-pointer -z-1">
                                                <label :for="'size-' + index" class="bg-porcelain rounded text-shuttle px-3 py-4 text-xs flex items-center justify-center leading-none cursor-pointer transition hover:bg-porcelain2">{{size.productSize}}</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- Right scroll indicator -->
                            <div v-if="isScrollable" class="absolute right-0 flex items-center pointer-events-none bg-gradient-to-l from-white to-transparent pl-8 pr-2 -mr-6 h-full">
                                <div :class="displayingView === 'sizes' ? '' : 'pb-9'">
                                    <svg class="w-4 h-4 text-shuttle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <!-- Tab Selector -->
                        <div class="relative" v-if="shouldShowTabs">
                            <!-- Border segments and carets dynamically positioned -->
                            <template v-for="(tab, index) in availableTabs">
                                <template v-if="displayingView === tab.value">
                                    <!-- Left border segment -->
                                    <span :key="'left-border-' + index" class="absolute top-0 h-px bg-iron2" :style="`left: -1.5rem; width: calc(${tab.position}% + 1.5rem - 8px);`"></span>
                                    <!-- Right border segment -->
                                    <span :key="'right-border-' + index" class="absolute top-0 h-px bg-iron2" :style="`left: calc(${tab.position}% + 8px); right: -1.5rem;`"></span>
                                    <!-- Left caret edge -->
                                    <span :key="'left-caret-' + index" class="absolute h-px bg-iron2" :style="`left: calc(${tab.position}% - 8px); top: 0; width: 11.3px; transform-origin: left top; transform: rotate(45deg);`"></span>
                                    <!-- Right caret edge -->
                                    <span :key="'right-caret-' + index" class="absolute h-px bg-iron2" :style="`left: calc(${tab.position}% + 0px); top: 8px; width: 11.3px; transform-origin: left top; transform: rotate(-45deg);`"></span>
                                    <!-- Caret -->
                                    <span 
                                        :key="'caret-' + index"
                                        class="absolute top-0 w-0 h-0 z-10"
                                        :style="`left: ${tab.position}%; transform: translateX(-50%); border-left: 8px solid transparent; border-right: 8px solid transparent; border-top: 8px solid #ffffff;`"
                                    ></span>
                                </template>
                            </template>
                            
                            <div class="flex bg-porcelain2 -mx-6 px-6 py-3">
                                <button 
                                    v-for="tab in availableTabs"
                                    :key="tab.value"
                                    @click="setDisplayingView(tab.value)"
                                    class="flex-1 py-2 text-xs font-bold transition-colors"
                                    :class="displayingView === tab.value ? 'text-lochmara' : 'text-shuttle hover:text-shark'"
                                >
                                    {{tab.label|t}}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            </div>

            <!-- Mobile Product Variation Description -->
            <div class="p-6">
              <div class="mt-3" v-if="productInfo.productPageCtaLink">
                <a :href="productInfo.productPageCtaLink.url" :target="productInfo.productPageCtaLink.target" class="w-full cursor-pointer flex items-center justify-center bg-lochmara font-bold text-xs text-white py-5 px-8">{{productInfo.productPageCtaLink.text}}</a>
              </div>
              <div v-if="currentProduct">
                <span class="block text-xs text-shuttle">{{currentProduct.excerptText}}</span>
              </div>
              <div v-if="availabilityMessage" :class="availabilityMessageClass">{{availabilityMessage}}</div>
            </div>
        </div>

        <!-- Desktop Design -->
        <div class="hidden md:block">
            <div class="flex items-center justify-center w-full min-h-480px px-5 relative">
                <picture v-if="currentProduct && currentProduct.productBannerImage.length" class="bg-center absolute bg-cover bg-no-repeat left-0 top-0 w-full h-full z-0 hero-picture" :style="currentBackgroundStyle">
                    <img class="opacity-0 invisible w-full block absolute left-0 top-0" :src="currentProduct.productBannerImage[0].url" :alt="currentProduct.title" />
                </picture>
                <div class="container mx-auto">
                    <div class="flex items-end min-h-560px relative z-20">
                        <div class="w-full text-white py-30">
                            <div class="flex flex-row h-full items-end">
                                <div class="w-6/12" v-if="productInfo">
                                    <div class="mr-auto max-w-260px -mb-54px">
                                        <div class="bg-white shadow-figure2">
                                            <ul class="text-shuttle text-13px">
                                                <li class="p-4 border-b border-blackhaze">
                                                    <strong class="font-bold text-lochmara text-base">{{'Product Variations'|t}}</strong>
                                                </li>
                                                <template v-for="(product, index) in this.productInfo.products">
                                                    <!-- Made to Order Products sub-header for multishake, before 3rd product -->
                                                    <li v-if="isShake && index === 2" :key="'header-' + product.slug" class="p-4 border-b-2 border-blackhaze" style="border-top: 1px solid #006FBA;">
                                                        <span class="font-bold text-lochmara text-xs">{{'Made to Order Products:'|t}}</span>
                                                    </li>
                                                    <li :key="product.slug" class="p-4 border-b border-blackhaze last:border-b-0" :class="{'bg-porcelain2': product.slug === currentProduct.slug}">
                                                        <router-link :to="getProductLink(product)" class="text-shuttle text-13px">{{product.title}}</router-link>
                                                    </li>
                                                </template>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div class="w-6/12" v-if="currentProduct">
                                    <div class="ml-auto max-w-430px -mb-54px">
                                        <div class="bg-white p-6 shadow-figure2">
                                            <div class="flex flex-col">
                                                <div class="mb-5">
                                                    <h2 class="block text-28px text-shark font-bold">{{currentProduct.title}}</h2>
                                                    <span class="block text-xs text-shuttle">{{currentProduct.excerptText}}</span>
                                                </div>
                                                <!-- Only show the toggle if sizes are defined AND colors are available (hidden for shake). -->
                                                <div class="mb-5" v-if="!isShake && currentProduct.availableProductSizes && currentProduct.availableProductSizes.length && (currentProduct.availableProductStains.length || currentProduct.availableProductColors.length)">
                                                    <span class="block text-shuttle mb-3">
                                                        <div class="flex -mx-1">
                                                            <div class="mx-1">
                                                                <button class="bg-porcelain2 rounded-full w-43px h-23px flex items-center" @click="displayingSizes = !displayingSizes">
                                                                    <span class="relative mx-2px w-full h-full">
                                                                        <span :class="displayingSizes ? 'translate-x-full' : ''" class="absolute inset-y-0 my-auto block bg-allports rounded-full w-19px h-19px transform transition-transform duration-250 accelerate hover:bg-orient"></span>
                                                                    </span>
                                                                </button>
                                                            </div>
                                                            <div class="mx-1">
                                                                <span class="text-shark font-bold text-xs" v-if="displayingSizes">{{'View colors'|t}}</span><span class="text-shark font-bold text-xs" v-else>{{"View sizes"|t}}</span>
                                                            </div>
                                                        </div>
                                                    </span>
                                                    <hr class="-mx-6 border-iron2" />
                                                </div>
                                                <div v-if="isShake || !displayingSizes">
                                                    <div v-if="currentProduct.availablePrimed">
                                                        <div class="mb-5">
                                                            <span class="block text-shuttle mb-1"><strong class="text-shark font-bold">{{"Primed"|t}}</strong></span>
                                                            <ul class="flex flex-wrap -mx-1 pd-color-picker">
                                                                <li class="m-1">
                                                                    <router-link :to="getSwatchLink(currentProduct, primedColor)" class="block w-30px h-30px p-2 rounded-full cursor-pointer relative pd-color-picker__pallete" :class="{'active': currentColor.uid === primedColor.uid}" :style="{backgroundColor: primedColor.color}"></router-link>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                    <div class="mb-5" v-if="hasOrderedProductColors">
                                                        <span class="block text-shuttle mb-1" v-if="displayedProductColor"><strong class="text-shark font-bold">{{'Color'|t}}: </strong>{{displayedProductColor.title}}</span>

                                                        <div class="pd-color-picker w-full">
                                                            <div v-if="orderedProductColorGroups.core.length" class="flex items-start justify-between">
                                                                <ul class="flex flex-wrap -mx-1">
                                                                    <li class="m-1" v-for="color in orderedProductColorGroups.core" :key="color.uid">
                                                                        <router-link :to="getSwatchLink(currentProduct, color)" class="block w-30px h-30px p-2 rounded-full cursor-pointer relative pd-color-picker__pallete" :class="{'active': currentColor.uid === color.uid}" :style="{backgroundColor: color.color}"></router-link>
                                                                    </li>
                                                                </ul>
                                                                <img
                                                                    v-if="showSpectrumPaletteLogo"
                                                                    :src="spectrumLogo"
                                                                    alt="Spectrum"
                                                                    class="block h-auto ml-4 mt-1 flex-shrink-0"
                                                                    style="width: 104px;"
                                                                />
                                                            </div>
                                                            <div v-else-if="showSpectrumPaletteLogo" class="flex justify-end mb-1">
                                                                <img
                                                                    :src="spectrumLogo"
                                                                    alt="Spectrum"
                                                                    class="block h-auto flex-shrink-0"
                                                                    style="width: 104px;"
                                                                />
                                                            </div>

                                                            <ul
                                                                v-if="orderedProductColorGroups.secondary.length"
                                                                class="grid gap-y-2"
                                                                :class="{'mt-2': orderedProductColorGroups.core.length}"
                                                                style="grid-template-columns: repeat(10, 30px); justify-content: space-between;"
                                                            >
                                                                <li v-for="color in orderedProductColorGroups.secondary" :key="color.uid">
                                                                    <router-link :to="getSwatchLink(currentProduct, color)" class="block w-30px h-30px p-2 rounded-full cursor-pointer relative pd-color-picker__pallete" :class="{'active': currentColor.uid === color.uid}" :style="{backgroundColor: color.color}"></router-link>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                    <div v-if="currentProduct.availableProductStains.length">
                                                        <div class="mb-5">
                                                            <span class="block text-shuttle mb-1"><strong class="text-shark font-bold">{{"Stain"|t}}</strong></span>
                                                            <ul class="flex flex-wrap -mx-1 pd-color-picker">
                                                                <li class="m-1" v-for="stain in currentProduct.availableProductStains" :key="stain.uid">
                                                                    <router-link :to="getSwatchLink(currentProduct, stain)" class="block w-30px h-30px p-2 rounded-full cursor-pointer relative pd-color-picker__pallete" :class="{'active': currentColor.uid === stain.uid}" :style="{backgroundColor: stain.color}"></router-link>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div v-else>
                                                    <div class="block text-shuttle mb-2">
                                                        <ul class="flex flex-wrap -mx-2 pd-size-picker">
                                                            <li class="px-2 w-6/12 mb-10px" v-for="(size, index) in currentProduct.availableProductSizes">
                                                                <div class="relative cursor-pointer pd-size-picker__item">
                                                                    <input :id="'size-' + index" type="radio" name="pd-sizes" class="absolute inset-0 left-0 top-0 w-full h-full opacity-0 cursor-pointer -z-1">
                                                                    <label :for="'size-' + index" class="bg-porcelain rounded text-shuttle px-3 py-2 text-xs flex items-center justify-center leading-none cursor-pointer transition hover:bg-porcelain2">{{size.productSize}}</label>
                                                                </div>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>
                                                <div class="mt-3" v-if="productInfo.productPageCtaLink">
                                                    <a :href="productInfo.productPageCtaLink.url" :target="productInfo.productPageCtaLink.target" class="w-full cursor-pointer flex items-center justify-center bg-lochmara font-bold text-xs text-white py-5 px-8">{{productInfo.productPageCtaLink.text}}</a>
                                                </div>
                                              <div v-if="availabilityMessage" :class="availabilityMessageClass">{{availabilityMessage}}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

    import spectrumLogo from '../../img/allura/spectrum-logo@2x.png';
    import { getCoreColorSlugs, groupColorsByCore, orderColorsByCore } from './colorOrdering.js';

    const MIN_COLORS_SCROLLABLE = 8;
    const MIN_SIZES_SCROLLABLE = 4;
    const primedColor = {
        title: 'Primed',
        slug: 'primed',
        typeHandle: 'primed',
        uid: 'primed',
        color: '#a1a79c'
    }
    const DEFAULT_AVAILABILITY_MESSAGE = 'Availability may vary by market. Please contact us to discuss how we can best meet your needs.';
    const BACKERBOARD_AVAILABILITY_MESSAGE = 'Available in New Mexico, Wyoming, Utah, Arizona, Idaho, Washington, Oregon, Nevada, California, Hawaii and Canada. Other regions may vary. Please connect with a local representative for more information.';

    export default {
        name: 'HeroProduct',
        components: {
        },
        data: () => {
            return {
                primedColor: primedColor,
                spectrumLogo: spectrumLogo,
                displayingSizes: false,
                displayingView: 'colors', // 'colors', 'primed', or 'sizes'
                currentColor: null,
                currentProduct: null,
                showProductDropdown: false
            }
        },
        watch: {
            '$route': function(newVal) {
                this.setCurrentProduct();
                this.setCurrentColor();
                this.showProductDropdown = false;
            },
        },
        beforeMount() {
            this.setCurrentProduct();
            this.setCurrentColor();
        },
        methods: {
            toggleProductDropdown() {
                this.showProductDropdown = !this.showProductDropdown;
            },
            getProductLink(product) {
                return {
                    name: this.$route.name ?? 'product',
                    params: {productSlug: product.slug}
                }
            },
            setDisplayingView(view) {
                if (view === 'colors') {
                    const isFinishSelected = this.currentColor
                        && (this.currentColor.typeHandle === 'productColor' || this.currentColor.typeHandle === 'productStain');
                    const defaultStain = this.currentProduct.availableProductStains[0];
                    if (!isFinishSelected && this.defaultProductColor) {
                        this.currentColor = this.defaultProductColor;
                        this.displayingView = 'colors';
                        this.displayingSizes = false;
                        this.replaceProductRoute({name: 'productColor', params: {productSlug: this.currentProduct.slug, colorSlug: this.defaultProductColor.slug}});
                        return;
                    }
                    if (!isFinishSelected && defaultStain) {
                        this.currentColor = defaultStain;
                        this.displayingView = 'colors';
                        this.displayingSizes = false;
                        this.replaceProductRoute({name: 'productStain', params: {productSlug: this.currentProduct.slug, colorSlug: defaultStain.slug}});
                        return;
                    }
                }

                if (view === 'primed' && this.currentProduct.availablePrimed) {
                    this.currentColor = primedColor;
                    this.displayingView = 'primed';
                    this.displayingSizes = false;
                    this.replaceProductRoute({name: 'productPrimed', params: {productSlug: this.currentProduct.slug}});
                    return;
                }

                this.displayingView = view;
                this.displayingSizes = view === 'sizes';
            },
            replaceProductRoute(route) {
                const params = route.params || {};
                const currentParams = this.$route.params || {};
                const isCurrentRoute = this.$route.name === route.name
                    && currentParams.productSlug === params.productSlug
                    && currentParams.colorSlug === params.colorSlug;

                if (!isCurrentRoute) {
                    this.$router.replace(route);
                }
            },
            /**
             * Generates a link for a color swatch such that the current color is maintained on the next product
             */
            getSwatchLink(product, swatch) {
                let routeName = null
                let params = {productSlug: product.slug}
                switch(swatch.typeHandle) {
                    case 'primed':
                        routeName = 'productPrimed'
                        break
                    case 'productColor':
                        routeName = 'productColor'
                        params.colorSlug = swatch.slug
                        break
                    case 'productStain':
                        routeName = 'productStain'
                        params.colorSlug = swatch.slug
                        break
                }
                return {
                    name: routeName,
                    params: params
                }
            },
            /**
             * Updates the current product based on the requested one
             */
            setCurrentProduct() {
                const productSlug = this.$route.params.productSlug;
                const products = this.productInfo.products;
                if(productSlug) {
                    const products = this.productInfo.products;
                    for (let index = 0; index < products.length; index++) {
                        if (products[index].slug === this.$route.params.productSlug) {
                            this.currentProduct = products[index];
                            return
                        }
                    }
                }
                this.currentProduct = products[0];
            },
            /**
             * Updates the current color based on the requested one and availability on the current product
             */
            setCurrentColor() {
                const colors = this.currentProduct.availableProductColors
                const stains = this.currentProduct.availableProductStains
                const availablePrimed = this.currentProduct.availablePrimed
                const productSlug = this.currentProduct.slug
                const defaultColor = this.defaultProductColor;
                const shouldReplaceDefaultRoute = this.$route.path !== '/';

                // If there are colors available and the user requested a color, match the slug to the available colors
                if (colors.length && this.$route.name === 'productColor') {
                    for (let index = 0; index < colors.length; index++) {
                        if (colors[index].slug === this.$route.params.colorSlug) {
                            this.currentColor = colors[index];
                            this.displayingView = 'colors';
                            this.displayingSizes = false;
                            return
                        }
                    }
                    // Requested color not found, use the first color available and update the route
                    if (!this.currentColor) {
                        this.currentColor = defaultColor;
                        this.displayingView = 'colors';
                        this.displayingSizes = false;
                        this.$router.replace({name: 'productColor', params: {productSlug, colorSlug: defaultColor.slug}})
                    }
                // If there are stains available and the user requested a stain, match the slug to the available stains
                } else if (stains.length && this.$route.name === 'productStain') {
                    for (let index = 0; index < stains.length; index++) {
                        if (stains[index].slug === this.$route.params.colorSlug) {
                            this.currentColor = stains[index];
                            this.displayingView = 'colors';
                            this.displayingSizes = false;
                            return
                        }
                    }
                    // Requested stain not found, use the first stain available and update the route
                    if (!this.currentColor) {
                        this.currentColor = stains[0];
                        this.displayingView = 'colors';
                        this.displayingSizes = false;
                        this.$router.replace({name: 'productStain', params: {productSlug, colorSlug: stains[0].slug}})
                    }
                // If the product is available primed and the user requested primed, set it to primed!
                } else if (availablePrimed && this.$route.name === 'productPrimed') {
                    this.currentColor = primedColor
                    this.displayingView = 'primed';
                    this.displayingSizes = false;
                } else {
                    // Default back to Primed when available. Otherwise show the next available product option.
                    if (availablePrimed) {
                        this.currentColor = primedColor;
                        this.displayingView = 'primed';
                        this.displayingSizes = false;
                        if (shouldReplaceDefaultRoute) {
                            this.$router.replace({name: 'productPrimed', params: {productSlug}})
                        }
                    } else if (colors.length) {
                        this.currentColor = defaultColor;
                        this.displayingView = 'colors';
                        this.displayingSizes = false;
                        if (shouldReplaceDefaultRoute) {
                            this.$router.replace({name: 'productColor', params: {productSlug, colorSlug: defaultColor.slug}})
                        }
                    } else if(stains.length) {
                        this.currentColor = stains[0];
                        this.displayingView = 'colors';
                        this.displayingSizes = false;
                        if (shouldReplaceDefaultRoute) {
                            this.$router.replace({name: 'productStain', params: {productSlug, colorSlug: stains[0].slug}})
                        }
                    } else if (!this.isShake && this.currentProduct.availableProductSizes && this.currentProduct.availableProductSizes.length) {
                        this.displayingView = 'sizes';
                        this.displayingSizes = true;
                    } else {
                        console.error('Something went wrong.')
                    }
                }
            }
        },

        computed: {
            isShake: function() {
                return this.productInfo.entrySlug === 'multishake';
            },
            isBackerboard: function() {
                const slugs = [
                    this.productInfo && this.productInfo.entrySlug,
                    this.currentProduct && this.currentProduct.slug
                ].filter(Boolean);

                return slugs.some(slug => slug === 'allura-backer' || slug.includes('backer'));
            },
            availabilityMessage: function() {
                return this.isBackerboard ? BACKERBOARD_AVAILABILITY_MESSAGE : DEFAULT_AVAILABILITY_MESSAGE;
            },
            availabilityMessageClass: function() {
                return this.isBackerboard
                    ? 'block bg-lochmara text-white mt-4 px-5 py-4 text-sm leading-relaxed'
                    : 'block text-shuttle mt-3 text-3xs';
            },
            /**
             * Returns the product's available colors with core Spectrum colors first.
             */
            orderedProductColors: function() {
                return [...this.orderedProductColorGroups.core, ...this.orderedProductColorGroups.secondary];
            },
            defaultProductColor: function() {
                if (!this.currentProduct || !this.currentProduct.availableProductColors) {
                    return null;
                }

                // Default to the first color in the displayed (core-first) order so the
                // active swatch matches what the user sees at the top of the picker.
                return orderColorsByCore(
                    this.currentProduct.availableProductColors,
                    getCoreColorSlugs(this.productInfo && this.productInfo.entrySlug),
                )[0] || null;
            },
            orderedProductColorGroups: function() {
                if (!this.currentProduct || !this.currentProduct.availableProductColors) {
                    return { core: [], secondary: [] };
                }
                return groupColorsByCore(
                    this.currentProduct.availableProductColors,
                    getCoreColorSlugs(this.productInfo && this.productInfo.entrySlug),
                );
            },
            hasOrderedProductColors: function() {
                return !!(this.orderedProductColorGroups.core.length || this.orderedProductColorGroups.secondary.length);
            },
            selectedProductColor: function() {
                return this.currentColor && this.currentColor.typeHandle === 'productColor'
                    ? this.currentColor
                    : null;
            },
            displayedProductColor: function() {
                return this.selectedProductColor || this.defaultProductColor;
            },
            showSpectrumPaletteLogo: function() {
                return this.hasOrderedProductColors;
            },
            /**
             * Show the Spectrum logo whenever the user has a Spectrum color selected
             * (any color, not just the core 5). The picker also lists stains and Primed
             * which are not Spectrum finishes, so we restrict to productColor entries.
             */
            showSpectrumLogo: function() {
                return this.displayingView === 'colors'
                    && !!this.selectedProductColor;
            },
            /**
             * Computes the background styling for the current product configuration
             * @returns {{background: string}|null}
             */
            currentBackgroundStyle: function() {
                if (!this.currentProduct) {
                    return null;
                }
                if (!this.currentColor) {
                    return {
                        background: 'url(' + this.currentProduct.productBannerImage[0].url + ') center/cover no-repeat',
                    }
                }
                return {
                    background: this.currentColor.color + ' url(' + this.currentProduct.productBannerImage[0].url + ') center/cover no-repeat',
                }
            },
            /**
             * Determines if tabs should be shown on mobile
             */
            shouldShowTabs: function() {
                if (!this.currentProduct) return false;
                const hasColors = this.currentProduct.availableProductColors?.length || this.currentProduct.availableProductStains?.length;
                const hasSizes = this.currentProduct.availableProductSizes?.length;
                const hasPrimed = this.currentProduct.availablePrimed;
                // Show tabs if there are at least 2 different options
                const optionCount = (hasColors ? 1 : 0) + (!this.isShake && hasSizes ? 1 : 0) + (hasPrimed ? 1 : 0);
                return optionCount >= 2;
            },
            /**
             * Returns available tabs with their positions
             */
            availableTabs: function() {
                if (!this.currentProduct) return [];
                const tabs = [];
                const hasColors = this.currentProduct.availableProductColors?.length || this.currentProduct.availableProductStains?.length;
                const hasSizes = this.currentProduct.availableProductSizes?.length;
                const hasPrimed = this.currentProduct.availablePrimed;
                
                if (this.isShake) {
                    if (hasPrimed) tabs.push({ value: 'primed', label: 'Primed' });
                    if (hasColors) tabs.push({ value: 'colors', label: 'Colors' });
                } else {
                    if (hasPrimed) tabs.push({ value: 'primed', label: 'Primed' });
                    if (hasColors) tabs.push({ value: 'colors', label: 'Colors' });
                    if (hasSizes) tabs.push({ value: 'sizes', label: 'Sizes' });
                }
                
                // Calculate positions based on number of tabs
                const tabCount = tabs.length;
                tabs.forEach((tab, index) => {
                    // Position at the center of each section
                    tab.position = ((index * 2 + 1) / (tabCount * 2)) * 100;
                });
                
                return tabs;
            },
            /**
             * Determines if the current view has scrollable content
             */
            isScrollable: function() {
                if (!this.currentProduct) return false;
                
                // For colors view, check if there are multiple colors or stains
                if (this.displayingView === 'colors') {
                    const colorCount = (this.currentProduct.availableProductColors?.length || 0) + 
                                     (this.currentProduct.availableProductStains?.length || 0);
                    return colorCount > MIN_COLORS_SCROLLABLE;
                }
                
                // For primed view, never scrollable (single item)
                if (this.displayingView === 'primed') {
                    return false;
                }
                
                // For sizes view, check if there are multiple sizes
                if (this.displayingView === 'sizes') {
                    const sizeCount = this.currentProduct.availableProductSizes?.length || 0;
                    return sizeCount > MIN_SIZES_SCROLLABLE;
                }
                
                return false;
            }
        },

        props: [
            'productInfo'
        ]
    }
</script>

<style scoped type="text/css">
    .hero-picture {
        transition: background-color 0.1s ease-in;
        background-size: cover;
    }
    
    /* Hide scrollbar for Chrome, Safari and Opera */
    .scrollbar-hide::-webkit-scrollbar {
        display: none;
    }
    
    /* Hide scrollbar for IE, Edge and Firefox */
    .scrollbar-hide {
        -ms-overflow-style: none;  /* IE and Edge */
        scrollbar-width: none;  /* Firefox */
    }
</style>
